/* eslint-disable yoda */
import {
    GimliCustomerData,
    LoginResponse,
    Portfolio,
    getAvailability,
    getGimliCustomerData,
    getGimliLogin,
} from '@vfde-sails/avc-core';
import {
    CUSTOMER_TYPE_DSL,
    CUSTOMER_TYPE_MOBILE,
    CUSTOMER_TYPE_NON_VF,
} from '@vfde-sails/constants';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { AppAction, ErrorType } from './enum';
import {
    setAvailableRouterIds,
    setCustomer,
    setCustomerType,
    setDisableBooking,
    setError,
    setLoading,
    setProviderType,
    setPublicFactName,
    setRouters,
} from './action';
import {
    getAvailableRouterIds,
    getCustomerType,
    getProviderType,
} from './util/customerHelper';
import { JarvisHomebox } from '../../interface';
import { getUrl } from '../../util/url';
import { Domains, Endpoints, QueryParam } from '../../enum';
import { getJarvisRouters } from '../../util/jarvis';
import { selectRouterIds, selectState } from './selector';
import { trackError, trackPageView } from '../../tracking/tracking';
import { AppState, PublicFactName } from './interface';
import { TrackingErrorType } from '@vfde-sails/tracking';

/**
 * Fetches all customer related information if the customer is logged in,
 * redirects the user if he is not eligible to use the application and
 * checks the availability of the routers
 * @todo Error handling
 */
export function* getCustomerSaga() {
    yield put(setLoading(true));
    const customerType = getCustomerType();

    if (!customerType) {
        yield put(setLoading(false));
        return;
    }

    if (customerType === CUSTOMER_TYPE_MOBILE
        || customerType === CUSTOMER_TYPE_NON_VF
        || customerType === CUSTOMER_TYPE_DSL) {
        yield put(setLoading(false));
        yield put(setDisableBooking(true));
        yield put(setCustomerType(customerType));
        return;
    }

    const providerType = getProviderType(customerType);

    if (!providerType) {
        return;
    }

    yield put(setProviderType(providerType));

    try {
        const response: LoginResponse | null = yield call(getGimliLogin);

        if (!response) {
            throw Error('Empty login response');
        }

        if (!response.success) {
            if (response.loginState === 'redirecting') {
                window.open(getUrl(Domains.ZUHAUSEPLUS + Endpoints.LOGIN_REDIRECT, {
                    [QueryParam.TARGET]: location.href,
                }), '_self');
                return;
            } else {
                throw Error('Login failed');
            }
        }

        const routerIds: string[] = yield select(selectRouterIds);
        const availability: Portfolio | undefined = yield call(getAvailability, routerIds);

        if (!availability) {
            throw Error('Empty availability response');
        }

        const publicFact = availability.publicFacts.find(fact => fact.value);

        if (publicFact) {
            yield put(setDisableBooking(true));
            yield put(setPublicFactName((publicFact.name) as PublicFactName));
        }

        const customerData: GimliCustomerData = yield call(getGimliCustomerData);
        yield put(setCustomer(customerData));

        yield put(setAvailableRouterIds(
            getAvailableRouterIds(availability, customerData.inventoryMetacodes)
        ));
    } catch (error) {
        yield put(setError({ error: error as Error, type: ErrorType.AVC }));
        yield call(trackError, error as Error, TrackingErrorType.Technical);
    } finally {
        yield put(setLoading(false));
    }
}

/**
 * Fetches all router product information in Jarvis
 * @todo Implement API call once Jarvis available
 */
export function* getRoutersSaga() {
    try {
        yield put(setLoading(true));

        const routers: JarvisHomebox[] = yield call(getJarvisRouters);

        yield put(setRouters(routers));
    } catch (error) {
        yield put(setError({ error: error as Error, type: ErrorType.JARVIS }));
        yield call(trackError, error as Error, TrackingErrorType.Technical);
    } finally {
        yield put(setLoading(false));
    }
}

/**
 * Track page view
 */
export function* trackPageViewSaga() {
    const state: AppState = yield select(selectState);
    yield call(trackPageView, state);
}

/**
 * Calls `getRoutersSaga` and `getCustomerSaga` to fetch all router
 * and customer related information
 */
export function* initStoreSaga() {
    yield put(setError(undefined));
    yield call(getRoutersSaga);
    yield call(getCustomerSaga);
    yield call(trackPageViewSaga);
}

export default function* saga() {
    yield takeLatest(AppAction.INIT_STORE, initStoreSaga);
    yield takeLatest(AppAction.SET_SELECTED_ROUTER, trackPageViewSaga);
}
